import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams, Link } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Technology from "@/pages/Technology";
import FindProfile from "@/pages/FindProfile";
import PostJob from "@/pages/PostJob";
import Auth from "@/pages/Auth";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ConfirmEmailChange from "@/pages/ConfirmEmailChange";
import CoachDashboard from "@/pages/CoachDashboard";
import CoachProfileEdit from "@/pages/CoachProfileEdit";
import Opportunities from "@/pages/Opportunities";
import ADDashboard from "@/pages/ADDashboard";
import JobNew from "@/pages/JobNew";
import JobDetail from "@/pages/JobDetail";
import PublicJobDetail from "@/pages/PublicJobDetail";
import CoachAlerts from "@/pages/CoachAlerts";
import CandidateDetail from "@/pages/CandidateDetail";
import MarketIntel from "@/pages/MarketIntel";
import Messages from "@/pages/Messages";
import TalentPool from "@/pages/TalentPool";
import ContractDraft from "@/pages/ContractDraft";
import Pricing from "@/pages/Pricing";
import WeeklyJobs from "@/pages/WeeklyJobs";
import AlertsManage from "@/pages/AlertsManage";
import CoachPilot from "@/pages/hq/CoachPilot";
import ProgramPilot from "@/pages/hq/ProgramPilot";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Alerts from "@/pages/Alerts";
import ClaimInbox from "@/pages/ClaimInbox";
import ClaimAccept from "@/pages/ClaimAccept";
import ClaimLanding from "@/pages/ClaimLanding";
import StealthSearch from "@/pages/StealthSearch";
import AdminOutreach from "@/pages/AdminOutreach";
import AdminSources from "@/pages/AdminSources";
import AdminInsights from "@/pages/AdminInsights";
import ClubBeta from "@/pages/ClubBeta";
import JobSearch from "@/pages/JobSearch";
import CIRExplainer from "@/pages/CIRExplainer";
import EndorsementForm from "@/pages/EndorsementForm";
import WhoWeAre from "@/pages/WhoWeAre";
import OnboardMagicLink from "@/pages/OnboardMagicLink";
import ListingClaim from "@/pages/ListingClaim";
import AdminCoaches from "@/pages/AdminCoaches";
import AdminCoachEdit from "@/pages/AdminCoachEdit";
import AdminJobEdit from "@/pages/AdminJobEdit";
import AdminCompensation from "@/pages/AdminCompensation";
import AdminNewsIngest from "@/pages/AdminNewsIngest";
import SocialStudio from "@/pages/SocialStudio";
import Unsubscribe from "@/pages/Unsubscribe";
import ColorLab from "@/pages/ColorLab";
import BetaDisclaimer from "@/components/BetaDisclaimer";
import HQLayout from "@/components/HQLayout";
import AdminHome from "@/pages/hq/AdminHome";
import WorkQueue from "@/pages/hq/WorkQueue";
import Campaigns from "@/pages/hq/Campaigns";
import Delivery from "@/pages/hq/Delivery";
import DataQuality from "@/pages/hq/DataQuality";
import AdminJobs from "@/pages/hq/AdminJobs";
import Programs from "@/pages/hq/Programs";
import SystemPage from "@/pages/hq/SystemPage";
import ReplyInbox from "@/pages/hq/ReplyInbox";
import Revenue from "@/pages/hq/Revenue";
import Funnel from "@/pages/hq/Funnel";

function RedirectWithParam({ to }) {
  const params = useParams();
  return <Navigate to={to(params)} replace />;
}

function Protected({ children, role, platformAdmin }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen bg-appbg text-white p-10 heading text-3xl">LOADING…</div>;
  if (!user) return <Navigate to={`/auth?mode=login&next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  if (platformAdmin && !user.is_platform_admin) return <Navigate to="/" replace />;
  return children;
}

// RoleTheme toggles the .role-program class on <body> whenever the signed-in
// user's role is athletic_director. Coach + logged-out users get the default
// arena palette (red-forward on gunmetal). Program users get the front-office
// palette (navy-forward on graphite). See index.css for the palette definitions.
// Public job detail for anonymous visitors (jobs-first); signed-in users keep the full JobDetail (apply, matches, admin tools).
function JobRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  // Coaches act on openings from the public detail (express interest = existing /coach/interests); programs/admins keep JobDetail.
  return user && user.role !== "coach" ? <JobDetail /> : <PublicJobDetail />;
}

function RoleTheme() {
  const { user } = useAuth();
  useEffect(() => {
    const cls = "role-program";
    const isProgram = user?.role === "athletic_director";
    document.body.classList.toggle(cls, isProgram);
    return () => document.body.classList.remove(cls);
  }, [user?.role]);
  return null;
}

function AdminEntry() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/auth?mode=login&next=/admin" replace />;
  if (user.is_platform_admin) return <Navigate to="/hq" replace />;
  return (
    <div className="min-h-screen bg-appbg text-white flex items-center justify-center p-8" data-testid="admin-denied">
      <div className="max-w-md border border-neutral-800 p-8"><div className="text-xs tracking-widest text-blaze">ADMIN ACCESS</div>
        <h1 className="heading text-3xl mt-2">THIS ACCOUNT ISN'T A PLATFORM ADMIN.</h1>
        <p className="text-sm text-neutral-400 mt-3">You're signed in as {user.email}. Admin tools are limited to platform staff; no roles are granted here. <Link to="/" className="underline">Back to CoachConnect</Link></p></div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <RoleTheme />
        <BetaDisclaimer />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/technology" element={<Technology />} />
          <Route path="/coaches/find" element={<FindProfile />} />
          <Route path="/post-job" element={<PostJob />} />
          <Route path="/color-lab" element={<ColorLab />} />
          <Route path="/who-we-are" element={<WhoWeAre />} />
          <Route path="/onboard/:token" element={<OnboardMagicLink />} />
          <Route path="/cir" element={<CIRExplainer />} />
          <Route path="/endorse/:token" element={<EndorsementForm />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/confirm-email-change" element={<ConfirmEmailChange />} />
          <Route path="/position-search" element={<Navigate to="/pricing" replace />} />
          <Route path="/claim/:token" element={<ClaimLanding />} />
          <Route path="/claim/:token/accept" element={<ClaimAccept />} />
          <Route path="/listing/claim/:token" element={<ListingClaim />} />
          <Route path="/payments/success" element={<PaymentSuccess />} />
          <Route path="/payments/cancel" element={<PaymentCancel />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/jobs/weekly" element={<WeeklyJobs />} />
          <Route path="/jobs" element={<WeeklyJobs />} />
          <Route path="/coach/alerts/email" element={<Protected role="coach"><CoachAlerts /></Protected>} />
          <Route path="/alerts/manage" element={<AlertsManage />} />
          <Route path="/admin" element={<AdminEntry />} />
          <Route path="/upgrade" element={<Pricing />} />

          {/* Coach routes */}
          <Route path="/coach" element={<Protected role="coach"><CoachDashboard /></Protected>} />
          <Route path="/coach/profile" element={<Protected role="coach"><CoachProfileEdit /></Protected>} />
          <Route path="/coach/opportunities" element={<Protected role="coach"><Opportunities /></Protected>} />
          <Route path="/coach/jobs" element={<Protected role="coach"><JobSearch /></Protected>} />
          <Route path="/coach/alerts" element={<Protected role="coach"><Alerts /></Protected>} />

          {/* AD routes */}
          <Route path="/ad" element={<Protected role="athletic_director"><ADDashboard /></Protected>} />
          <Route path="/ad/jobs/new" element={<Protected role="athletic_director"><JobNew /></Protected>} />
          <Route path="/jobs/new" element={<Navigate to="/ad/jobs/new" replace />} />
          <Route path="/ad/market" element={<Protected role="athletic_director"><MarketIntel /></Protected>} />
          <Route path="/ad/talent" element={<Protected role="athletic_director"><TalentPool /></Protected>} />
          <Route path="/ad/invites" element={<Protected role="athletic_director"><ClaimInbox /></Protected>} />
          <Route path="/ad/stealth" element={<Protected role="athletic_director"><StealthSearch /></Protected>} />
          <Route path="/ad/marketing" element={<Navigate to="/hq" replace />} />
          <Route path="/ad/marketing/outreach" element={<Navigate to="/hq/outreach" replace />} />
          <Route path="/ad/marketing/sources" element={<Navigate to="/hq/sources" replace />} />
          <Route path="/ad/marketing/insights" element={<Navigate to="/hq/analytics" replace />} />
          <Route path="/club" element={<ClubBeta />} />
          <Route path="/ad/marketing/coaches" element={<Navigate to="/hq/coaches" replace />} />
          <Route path="/ad/marketing/coaches/:coachId/edit" element={<RedirectWithParam to={(p) => `/hq/coaches/${p.coachId}/edit`} />} />
          <Route path="/ad/marketing/jobs/new" element={<Navigate to="/hq/jobs/new" replace />} />
          <Route path="/ad/marketing/jobs/:jobId/edit" element={<RedirectWithParam to={(p) => `/hq/jobs/${p.jobId}/edit`} />} />
          <Route path="/ad/marketing/compensation" element={<Navigate to="/hq/compensation" replace />} />
          <Route path="/ad/marketing/news-ingest" element={<Navigate to="/hq/news-ingest" replace />} />
          <Route path="/ad/marketing/emails" element={<Navigate to="/hq/queue" replace />} />
          <Route path="/ad/marketing/social" element={<Navigate to="/hq/social" replace />} />

          {/* Admin HQ — operator console */}
          <Route path="/hq" element={<Protected platformAdmin><HQLayout /></Protected>}>
            <Route path="coach-pilot" element={<CoachPilot />} />
            <Route path="program-pilot" element={<ProgramPilot />} />
            <Route index element={<AdminHome />} />
            <Route path="queue" element={<WorkQueue />} />
            <Route path="inbox" element={<ReplyInbox />} />
            <Route path="campaigns" element={<Campaigns />} />
            <Route path="outreach" element={<AdminOutreach />} />
            <Route path="delivery" element={<Delivery />} />
            <Route path="coaches" element={<AdminCoaches />} />
            <Route path="coaches/:coachId/edit" element={<AdminCoachEdit />} />
            <Route path="jobs" element={<AdminJobs />} />
            <Route path="jobs/new" element={<AdminJobEdit isNew />} />
            <Route path="jobs/:jobId/edit" element={<AdminJobEdit />} />
            <Route path="programs" element={<Programs />} />
            <Route path="compensation" element={<AdminCompensation />} />
            <Route path="data-quality" element={<DataQuality />} />
            <Route path="sources" element={<AdminSources />} />
            <Route path="news-ingest" element={<AdminNewsIngest />} />
            <Route path="analytics" element={<AdminInsights />} />
            <Route path="revenue" element={<Revenue />} />
            <Route path="funnel" element={<Funnel />} />
            <Route path="social" element={<SocialStudio />} />
            <Route path="system" element={<SystemPage />} />
          </Route>

          {/* Shared */}
          <Route path="/jobs/:jobId" element={<JobRoute />} />
          <Route path="/coaches/:coachId" element={<Protected><CandidateDetail /></Protected>} />
          <Route path="/contracts/:coachId" element={<Protected role="athletic_director"><ContractDraft /></Protected>} />
          <Route path="/messages" element={<Protected><Messages /></Protected>} />
          <Route path="/messages/:threadKey" element={<Protected><Messages /></Protected>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster theme="dark" position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
